import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';

// assets
import { ShieldTick, ShieldCross, Warning2 } from 'iconsax-react';

// project-imports
import api from 'api/api';
import serverCall from 'api/serverCall';
import WebSocketService from 'websocket/config';
import { useWebsocketContext } from 'contexts/WebSocketContext';
import { Tick } from 'types/custom/websocket';

// ==============================|| HEADER - INDEX SAFETY TICKER ||============================== //

type SignalLevel = 'SAFE' | 'CAUTION' | 'NOT_SAFE';

type TickerIndex = {
  stockId: string;
  label: string;
};

type NoTradeSnapshot = {
  spotPrice?: number;
  spotLastDayClose?: number;
  totalRiskScore?: number;
  finalRiskLevel?: string;
  tradeAllowed?: boolean;
  indicators?: Record<string, number | string | null | undefined>;
};

const INDEXES: TickerIndex[] = [
  { stockId: 'NIFTY', label: 'NIFTY' },
  { stockId: 'SENSEX', label: 'SENSEX' }
];

const signalColors: Record<SignalLevel, string> = {
  SAFE: '#22c55e',
  CAUTION: '#f59e0b',
  NOT_SAFE: '#ef4444'
};

const signalTooltip: Record<SignalLevel, string> = {
  SAFE: 'Safe to trade',
  CAUTION: 'Trade with caution',
  NOT_SAFE: 'Not safe to trade'
};

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
};

// Ticks report 0 before the first real trade of the session; treat that as "no price yet".
const toLivePriceOrNull = (value: unknown): number | null => {
  const asNumber = toNumberOrNull(value);
  return asNumber === null || asNumber === 0 ? null : asNumber;
};

const getIndicatorRawValue = (indicators: NoTradeSnapshot['indicators'], keys: string[]) => {
  if (!indicators) return undefined;
  for (const key of keys) {
    if (indicators[key] !== null && indicators[key] !== undefined && indicators[key] !== '') return indicators[key];
  }
  return undefined;
};

const normalizeSignalLevel = (value?: string): SignalLevel | undefined => {
  if (!value) return undefined;
  const normalized = value.toUpperCase().replace('-', '_');
  if (normalized === 'SAFE') return 'SAFE';
  if (normalized === 'CAUTION') return 'CAUTION';
  if (normalized === 'NOT_SAFE' || normalized === 'UNSAFE') return 'NOT_SAFE';
  return undefined;
};

const getSignalLevelFromScore = (score: number, hardLevel?: SignalLevel): SignalLevel => {
  if (hardLevel === 'NOT_SAFE') return 'NOT_SAFE';
  if (hardLevel === 'CAUTION') return 'CAUTION';
  if (score >= 6) return 'NOT_SAFE';
  if (score >= 3) return 'CAUTION';
  return 'SAFE';
};

const SignalIcon = ({ level, size }: { level: SignalLevel; size: number }) => {
  if (level === 'SAFE') return <ShieldTick variant="Bold" size={size} color={signalColors.SAFE} />;
  if (level === 'CAUTION') return <Warning2 variant="Bold" size={size} color={signalColors.CAUTION} />;
  return <ShieldCross variant="Bold" size={size} color={signalColors.NOT_SAFE} />;
};

const IndexTile = ({ index, snapshot, tick }: { index: TickerIndex; snapshot?: NoTradeSnapshot; tick?: Tick }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Live tick wins over the periodic no-trade snapshot, which only refreshes with each decision.
  const spot =
    toLivePriceOrNull(tick?.price) ??
    snapshot?.spotPrice ??
    toNumberOrNull(getIndicatorRawValue(snapshot?.indicators, ['spotPrice', 'spot', 'underlyingPrice']));
  const lastDayClose =
    toLivePriceOrNull(tick?.prevClose) ??
    snapshot?.spotLastDayClose ??
    toNumberOrNull(getIndicatorRawValue(snapshot?.indicators, ['spotLastDayClose', 'underlyingLastDayClose']));

  const level: SignalLevel =
    snapshot?.tradeAllowed === false
      ? normalizeSignalLevel(snapshot?.finalRiskLevel) ?? 'NOT_SAFE'
      : getSignalLevelFromScore(snapshot?.totalRiskScore ?? 0, normalizeSignalLevel(snapshot?.finalRiskLevel));

  const price =
    spot === null || spot === undefined ? '--' : spot.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  let change = '';
  let changeColor = theme.palette.text.secondary;
  // The tile is tinted by the day's move — the thing that changes tick by tick — while the
  // icon carries the no-trade signal. Two separate readings, so one never masks the other.
  let tileColor: string | null = null;
  if (spot != null && lastDayClose != null && lastDayClose !== 0) {
    const diff = spot - lastDayClose;
    const diffPct = (diff / lastDayClose) * 100;
    change = `${diff >= 0 ? '+' : ''}${diff.toFixed(2)} (${diffPct.toFixed(2)}%)`;
    changeColor = diff >= 0 ? signalColors.SAFE : signalColors.NOT_SAFE;
    tileColor = changeColor;
  }
  // Before the first tick there is no move to show, so the tile stays neutral.
  const tint = tileColor ?? theme.palette.text.disabled;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      onClick={() => navigate(`/no-trade-indicator?index=${index.stockId}`)}
      data-tooltip-id="header-tooltip"
      data-tooltip-content={`${index.label} - ${signalTooltip[level]}`}
      sx={{
        cursor: 'pointer',
        px: 1.75,
        py: 1,
        borderRadius: 2,
        border: `1px solid ${alpha(tint, 0.35)}`,
        bgcolor: alpha(tint, 0.08),
        '&:hover': { bgcolor: alpha(tint, 0.16) }
      }}
    >
      <SignalIcon level={level} size={18} />
      <Box sx={{ lineHeight: 1.1 }}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            {index.label}
          </Typography>
          {/* Stated outright: the header bar sets its own text colour, and inheriting it
              left the price white on white. */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {price}
          </Typography>
        </Stack>
        {change && (
          <Typography variant="caption" sx={{ color: changeColor, fontSize: 10 }}>
            {change}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

const IndexSafetyTicker = () => {
  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const { liveTicks } = useWebsocketContext();
  const [snapshots, setSnapshots] = useState<Record<string, NoTradeSnapshot>>({});
  const [ticks, setTicks] = useState<Record<string, Tick | undefined>>({});

  // Ticks land on a ref (by design, to avoid re-rendering the tree on every tick),
  // so sample it on a timer and re-render only these two tiles.
  useEffect(() => {
    const readTicks = () => {
      setTicks((previous) => {
        let changed = false;
        const next: Record<string, Tick | undefined> = { ...previous };

        INDEXES.forEach((item) => {
          const tick = liveTicks.current?.[item.stockId];
          if (tick?.price !== previous[item.stockId]?.price || tick?.prevClose !== previous[item.stockId]?.prevClose) {
            next[item.stockId] = tick;
            changed = true;
          }
        });

        return changed ? next : previous;
      });
    };

    readTicks();
    const intervalId = window.setInterval(readTicks, 1000);

    return () => window.clearInterval(intervalId);
  }, [liveTicks]);

  const applySnapshot = useCallback((stockId: string, snapshot: NoTradeSnapshot) => {
    setSnapshots((previous) => ({ ...previous, [stockId]: { ...previous[stockId], ...snapshot } }));
  }, []);

  const fetchLatest = useCallback(
    (stockId: string) => {
      serverCall.get<NoTradeSnapshot[]>(
        api.getTodayNoTradeDecisions(stockId),
        (response, error) => {
          if (error || !Array.isArray(response) || response.length === 0) return;
          applySnapshot(stockId, response[response.length - 1]);
        },
        false,
        { source: 'admin' }
      );
    },
    [applySnapshot]
  );

  useEffect(() => {
    INDEXES.forEach((item) => fetchLatest(item.stockId));

    const intervalId = window.setInterval(() => {
      INDEXES.forEach((item) => fetchLatest(item.stockId));
    }, 5 * 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [fetchLatest]);

  useEffect(() => {
    const listeners = INDEXES.map((item) => ({
      topic: `/topic/no-trade-indicator/NDI_${item.stockId}`,
      handler: (message: any) => {
        if (!message) return;
        applySnapshot(item.stockId, {
          spotPrice: message.spotPrice,
          spotLastDayClose: message.spotLastDayClose,
          totalRiskScore: message.totalRiskScore ?? message.totalScore,
          finalRiskLevel: message.finalRiskLevel ?? message.finalLevel,
          tradeAllowed: message.tradeAllowed,
          indicators: message.indicators
        });
      }
    }));

    WebSocketService.connect()
      .then(() => {
        listeners.forEach(({ topic, handler }) => WebSocketService.subscribe(topic, handler));
      })
      .catch((error) => {
        console.error('Unable to subscribe no-trade websocket topic', error);
      });

    return () => {
      listeners.forEach(({ topic, handler }) => WebSocketService.unsubscribe(topic, handler));
    };
  }, [applySnapshot]);

  if (downLG) return null;

  return (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      {INDEXES.map((item) => (
        <IndexTile key={item.stockId} index={item} snapshot={snapshots[item.stockId]} tick={ticks[item.stockId]} />
      ))}
    </Stack>
  );
};

export default IndexSafetyTicker;
